/**
 * Tenant theme applicator (issue #7).
 *
 * Adapted from standalone-search's theme-store but with the localStorage
 * persistence layer REMOVED. Theming is tenant-authoritative: the active
 * skin is always the subdomain-resolved tenant slug, never a stored user pick.
 *
 * Call `applyTenantTheme()` once in main.tsx BEFORE the first React render
 * to avoid a flash of the wrong theme. No zustand store is needed because
 * there is nothing reactive to track — the attribute is set synchronously
 * and never changes in the current session.
 */
import { tenantSlugFromHostname } from "@/lib/tenant";
import { resolveTenantTheme, type TenantSlug } from "@/lib/theme-presets";

/**
 * Set `data-theme-preset` on `<html>` from the exact hostname registry.
 * Safe to call before the DOM is hydrated (guards with typeof document check).
 * Returns the resolved slug so callers can use it if needed.
 */
export function applyTenantTheme(slug?: string): TenantSlug | null {
  const resolved = resolveTenantTheme(slug ?? tenantSlugFromHostname());
  if (typeof document !== "undefined") {
    if (resolved) {
      document.documentElement.setAttribute("data-theme-preset", resolved);
    } else {
      document.documentElement.removeAttribute("data-theme-preset");
    }
  }
  return resolved;
}
